import { createStyles, Stack } from '@mantine/core';
import { memo, useCallback, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { SmallLoadingSpinner } from '../../../components/LoadingSpinner';
import type { EnhancedNotification, IInboxListProps } from '../types';
import { InboxListHeader } from './InboxListHeader';
import { InboxListItem } from './InboxListItem';

const useStyles = createStyles((theme) => ({
	container: {
		width: 360,
		minWidth: 360,
		height: '100vh',
		borderRightWidth: 1,
		borderRightStyle: 'solid',
		borderRightColor: theme.other.getColor('border/secondary/default'),
	},
	listContainer: {
		marginLeft: theme.spacing.sm,
		marginRight: theme.spacing['4xs'],
		flex: 1,
		height: '100%',
		minHeight: 300,
	},
	virtuosoContainer: {
		'&::-webkit-scrollbar': {
			width: theme.spacing.xs,
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: theme.other.getColor('fill/tertiary/default'),
			borderRadius: theme.radius.sm,
			border: `2px solid transparent`,
			backgroundClip: 'padding-box',
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		scrollbarColor: `${theme.other.getColor('fill/tertiary/default')} transparent`,
		scrollbarWidth: 'thin',
		scrollbarGutter: 'stable',
		paddingBottom: theme.spacing.md,
	},
	noMoreNotifications: {
		textAlign: 'center',
		padding: theme.spacing.md,
		color: theme.other.getColor('text/secondary/default'),
	},
}));

function InboxListComponent({
	data,
	selected,
	selectedIndex,
	searchTerm,
	setSearchTerm,
	events,
	setEvents,
	isInclusive,
	setIsInclusive,
	hasNextPage,
	fetchNextPage,
	isFetchingNextPage,
	inboxEventTypes,
	opened,
	setOpened,
	handleDeleteAll,
	handleDeleteAllRead,
	handleMarkAllAsRead,
	handleDelete,
	handleMarkAsRead,
	handleRestore,
	navigate,
	user,
	workspace,
	onSearchClick,
	isLoading,
	filterOpened,
	setFilterOpened,
	filterSearchTerm,
	setFilterSearchTerm,
	unreadNotifications,
	handleOnSelect,
	onKeyDown,
	enhancedNotifications,
	queryClient,
	notificationSettingsData,
	updateNotificationSettings,
	markAsReadMutation,
	restoreAllNotifications,
}: IInboxListProps) {
	const { classes } = useStyles();

	const virtuosoRef = useRef<VirtuosoHandle | null>(null);
	const hasScrolledRef = useRef<boolean>(false);

	// Ref callback to scroll after mount without custom hooks/effects
	const setVirtuosoRef = useCallback(
		(node: VirtuosoHandle | null) => {
			virtuosoRef.current = node;
			if (node && (selectedIndex ?? -1) >= 0 && !hasScrolledRef.current) {
				hasScrolledRef.current = true;
				setTimeout(() => {
					node.scrollToIndex({
						index: selectedIndex as number,
						align: 'center',
						behavior: 'auto',
					});
				}, 0);
			}
		},
		[selectedIndex]
	);

	const handleEndReached = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage?.();
		}
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const renderItem = useCallback(
		(index: number, notification: EnhancedNotification) => (
			<InboxListItem
				key={notification.id}
				item={notification}
				selected={selected?.id === notification.id}
				onSelect={handleOnSelect}
				onKeyDown={onKeyDown}
				handleDelete={handleDelete}
				handleMarkAsRead={handleMarkAsRead}
				handleRestore={handleRestore}
				queryClient={queryClient}
				notificationSettingsData={notificationSettingsData}
				updateNotificationSettings={updateNotificationSettings}
				user={user}
				workspace={workspace}
			/>
		),
		[
			selected?.id,
			handleOnSelect,
			onKeyDown,
			handleDelete,
			handleMarkAsRead,
			handleRestore,
			queryClient,
			notificationSettingsData,
			updateNotificationSettings,
			user,
			workspace,
		]
	);

	const renderFooter = useCallback(() => {
		if (isFetchingNextPage) {
			return <SmallLoadingSpinner />;
		}
		return null;
	}, [isFetchingNextPage]);

	return (
		<Stack className={classes.container} spacing={0}>
			<InboxListHeader
				searchTerm={searchTerm}
				setSearchTerm={setSearchTerm}
				events={events}
				setEvents={setEvents}
				isInclusive={isInclusive}
				setIsInclusive={setIsInclusive}
				selected={selected}
				opened={opened}
				setOpened={setOpened}
				handleDeleteAll={handleDeleteAll}
				handleDeleteAllRead={handleDeleteAllRead}
				handleMarkAllAsRead={handleMarkAllAsRead}
				handleDelete={handleDelete}
				handleMarkAsRead={handleMarkAsRead}
				navigate={navigate}
				user={user}
				workspace={workspace}
				inboxEventTypes={inboxEventTypes}
				onSearchClick={onSearchClick}
				filterOpened={filterOpened}
				setFilterOpened={setFilterOpened}
				filterSearchTerm={filterSearchTerm}
				setFilterSearchTerm={setFilterSearchTerm}
				notifications={data}
				unreadNotifications={unreadNotifications || []}
				markAsRead={markAsReadMutation}
				restoreAllNotifications={restoreAllNotifications}
			/>
			<div className={classes.listContainer}>
				{isLoading && data.length === 0 ? (
					<SmallLoadingSpinner />
				) : (
					<Virtuoso
						ref={setVirtuosoRef}
						data={enhancedNotifications}
						defaultItemHeight={80}
						computeItemKey={(index, item: EnhancedNotification) => item.id}
						increaseViewportBy={{ top: 200, bottom: 600 }}
						scrollSeekConfiguration={{
							enter: (v) => Math.abs(v) > 1500,
							exit: (v) => Math.abs(v) < 30,
						}}
						itemContent={renderItem}
						endReached={handleEndReached}
						components={{
							Footer: renderFooter,
						}}
						className={classes.virtuosoContainer}
					/>
				)}
			</div>
		</Stack>
	);
}

export const InboxList = memo(InboxListComponent);
